import ReducersIndex from "reduxModules/ducks";

/**
* @module TutorialConfiguration
* @description Configuration module defining tutorial steps and associated actions
* for the conference room designer guided tour.
* 
* @exports {Object} tutorialActions - Action handlers for tutorial steps
* @exports {Array} tutorialSteps - Ordered array of tutorial step configurations
* 
* @description
* Tutorial Structure:
* 1. Action Handlers:
*    - setCeiling: Initializes ceiling tile configuration
*    - setTable: Sets up default table layout
*    Each handler:
*    - Checks current state
*    - Makes necessary updates
*    - Returns reset state
* 
* 2. Tutorial Steps:
*    Each step contains:
*    - content: Display text
*    - image?: Optional image path
*    - target?: Element ID(s) for spotlight
*    - menu?: Menu to activate
*    - action?: Action to trigger
* 
* @example
* // Action handler usage
* const resetState = tutorialActions.setCeiling(area, dispatch);
* 
* // Step structure
* {
*   content: "Step description",
*   image: "path/to/image.png",
*   target: "elementId",
*   menu: "menuName",
*   action: "actionName"
* }
* 
* @note
* Tutorial Flow:
* 1. Introduction
* 2. Room setup
* 3. Floor plan editing
* 4. Ceiling configuration
* 5. Furnishing placement
* 6. Acoustics setup
* 7. Microphone configuration
* 8. Speaker setup
* 9. Equipment selection
* 10. Action controls
* 11. Room management
* 12. Project actions
* 
* Action Handlers:
* - Maintain state consistency
* - Support reset functionality
* - Handle edge cases
* - Return reset data
* 
* Integration Points:
* - Uses Redux dispatch
* - Manages area state
* - Controls UI elements
* - Handles spotlight targeting
*/
const tutorialActions = {
  setCeiling: (area, dispatch) => {
    const currentCeiling = area.ceilingTiles;
    const hasCeiling = currentCeiling.tile !== 'no_ceiling_tiles';
    if (!hasCeiling) {
      dispatch(ReducersIndex.container.setCeilingTiles({ tile: "2'_x_2'" }));
    };
    return { ceilingTiles: currentCeiling }
  },

  setTable: (area, dispatch) => {
    const currentTable = area.table;
    const hasTable = currentTable.shape;
    if (!hasTable) {
      dispatch(ReducersIndex.container.setTable({ shape: "boat" }));
    };
    return { table: currentTable }
  }
};

const tutorialSteps = [
  {
    content: "Before you start designing, we want to take you through a quick tutorial that highlights the features of this conference room designer tool.",
    target: null
  }, {
    image: 'images/CRD-Area-Modal.png',
    content: "Enter your room information into the three pop up dialogs. Don't worry if you make mistakes, we can change these parameters later.",
    target: null
  }, {
    content: "In the FLOORPLAN menu, you can edit room parameters. You can also move the door as needed.",
    menu: 'floor_plan',
    target: 'floor_plan'
  }, {
    content: "The CEILING Menu allows you to modify the arrangement of ceiling tiles in the room. In this mode, you can also select tiles that are blocked. The calculator will avoid placing mics or speakers on blocked ceiling tiles.",
    menu: 'ceiling',
    target: 'ceiling',
    action: 'setCeiling'
  }, {
    content: "In the FURNISHING Menu, you can add furniture to the room. You can also remove chairs and move furniture around the room.",
    menu: 'furnishing',
    target: 'furnishing',
    action: 'setTable'
  }, {
    content: "The ACOUSTICS menu allows you to estimate the acoustic quality of your room. The calculator uses this to adjust the quantity and spacing of microphones.",
    menu: 'acoustics',
    target: 'acoustics'
  }, {
    content: 'The MICROPHONES menu allows you to select the desired microphone type and mic coverage. "FULL ROOM" covers the entire room, and "FOCUSED" limits coverage to the area likely to contain people.',
    menu: 'microphones',
    target: 'microphones'
  }, {
    content: "The SPEAKERS menu allows you to select whether the speakers should cover the entire room, or just the area likely to contain people.",
    menu: 'speakers',
    target: 'speakers'
  }, {
    content: "The EQUIPMENT LIST allows you to select which product family you want to use and lists the required equipment and positioning tables for each device type.",
    menu: 'equipmentlist',
    target: 'equipment_list'
  }, {
    content: "You can also customize your design by selecting an action from the mouse action buttons. Here you can MEASURE, MOVE, ADD, DELETE or LISTEN to what the microphone will sound like. Each Menu has its own set of action buttons.",
    menu: 'microphones',
    target: 'actionButtons'
  }, {
    content: "You can add more rooms to your project by clicking ADD A ROOM.",
    target: 'addRoom'
  }, {
    content: "Click REQUEST QUOTE to have a Biamp expert contact you about your project, click SAVE to generate a link to share your design or click EXPORT to create a PDF overview of your design parameters.",
    target: ['getQuote', 'saveButton', 'exportButton'],
  }
]

export default tutorialSteps;
export { tutorialActions };
